import React from 'react';
import PropTypes from 'prop-types';
import { trackEvent } from '../amplitude';
import '../styles/Pagination.css';

export const PAGINATION_ELLIPSIS = 'ellipsis';

export const getPaginationItems = (currentPage, totalPages) => {
  const safeTotal = Math.max(1, Number(totalPages) || 1);
  const safeCurrent = Math.min(Math.max(1, Number(currentPage) || 1), safeTotal);

  if (safeTotal <= 4) {
    return Array.from({ length: safeTotal }, (_, index) => index + 1);
  }

  const pages = new Set([
    1,
    safeTotal,
    safeCurrent - 1,
    safeCurrent,
    safeCurrent + 1,
  ]);

  if (safeCurrent <= 2) {
    pages.add(2);
    pages.add(3);
  }

  if (safeCurrent >= safeTotal - 1) {
    pages.add(safeTotal - 2);
    pages.add(safeTotal - 1);
  }

  const sortedPages = [...pages]
    .filter((page) => page >= 1 && page <= safeTotal)
    .sort((left, right) => left - right);

  return sortedPages.reduce((items, page, index) => {
    const previousPage = sortedPages[index - 1];

    if (index > 0 && page - previousPage > 1) {
      items.push(PAGINATION_ELLIPSIS);
    }

    items.push(page);
    return items;
  }, []);
};

const Pagination = ({
  currentPage,
  totalPages = null,
  hasPrevious,
  hasNext = false,
  onPageChange,
  disabled = false,
  className = '',
  compact = false,
  sequential = false,
  canNavigateToPage = null,
  pageType = 'news_list',
  category,
  section,
}) => {
  const knownTotalPages = Number(totalPages) > 0 ? Number(totalPages) : null;
  const previousAvailable = hasPrevious ?? currentPage > 1;
  const nextAvailable = knownTotalPages
    ? currentPage < knownTotalPages
    : Boolean(hasNext);
  const pageItems = knownTotalPages
    ? getPaginationItems(currentPage, knownTotalPages)
    : [currentPage];

  const isPageAvailable = (page) => {
    if (page === currentPage) return false;
    if (sequential && Math.abs(page - currentPage) > 1) return false;
    return canNavigateToPage ? canNavigateToPage(page) : true;
  };

  const changePage = (page) => {
    if (disabled || !isPageAvailable(page)) return;

    trackEvent('pagination_clicked', {
      page_type: pageType,
      from_page: currentPage,
      to_page: page,
      category,
      section,
    });
    onPageChange(page);
  };

  if (knownTotalPages === 1 && currentPage === 1 && !hasNext) return null;

  const rootClassName = [
    'pagination',
    compact ? 'pagination--compact' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <nav className={rootClassName} aria-label="Навігація сторінками">
      <button
        type="button"
        className="pagination__control pagination__arrow"
        aria-label="Попередня сторінка"
        disabled={disabled || !previousAvailable}
        onClick={() => changePage(currentPage - 1)}
      >
        ←
      </button>

      {pageItems.map((item, index) => {
        if (item === PAGINATION_ELLIPSIS) {
          return (
            <span
              className="pagination__ellipsis"
              aria-hidden="true"
              key={`ellipsis-${index}`}
            >
              …
            </span>
          );
        }

        const isCurrent = item === currentPage;
        const isEndpoint = item === 1 || item === knownTotalPages;
        const pageAvailable = isPageAvailable(item);

        return (
          <button
            type="button"
            className={`pagination__control pagination__page${
              isCurrent ? ' pagination__page--active' : ''
            }${isEndpoint ? ' pagination__page--endpoint' : ''}`}
            aria-current={isCurrent ? 'page' : undefined}
            aria-label={`Сторінка ${item}`}
            disabled={disabled || isCurrent || !pageAvailable}
            key={item}
            onClick={() => changePage(item)}
          >
            {item}
          </button>
        );
      })}

      <button
        type="button"
        className="pagination__control pagination__arrow"
        aria-label="Наступна сторінка"
        disabled={disabled || !nextAvailable}
        onClick={() => changePage(currentPage + 1)}
      >
        →
      </button>
    </nav>
  );
};

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number,
  hasPrevious: PropTypes.bool,
  hasNext: PropTypes.bool,
  onPageChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  compact: PropTypes.bool,
  sequential: PropTypes.bool,
  canNavigateToPage: PropTypes.func,
  pageType: PropTypes.string,
  category: PropTypes.string,
  section: PropTypes.string,
};

export default Pagination;
